import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getMeetingDisplayName,
  formatScheduledAt,
  formatDuration,
  STATUS_CONFIG,
  getAttendeeRoleLabel
} from '../utils/meetingTemplates';
import AddAttendeesModal from './AddAttendeesModal';
import { arMittMote } from '../utils/mittMote';

const MeetingCard = ({ meeting, onClick, onReschedule, onDelete, onAddNote, onOpenProtocol, onAttendeesAdded, showProject = false, notesCount = 0 }) => {
  const { user } = useAuth();
  const statusConfig = STATUS_CONFIG[meeting.status] || STATUS_CONFIG.draft;
  const [showAddAttendees, setShowAddAttendees] = useState(false);

  // Hitta användarens attendee-info
  const getUserAttendee = () => {
    if (meeting.status !== 'proposed') return null;
    return meeting.attendees?.find(a =>
      a.userId === user?._id ||
      a.userId?._id === user?._id ||
      a.email === user?.email
    );
  };

  // Kolla om alla deltagare (utom avsändaren) har svarat
  const checkAllResponded = () => {
    if (meeting.status !== 'proposed') return false;
    const senderId = meeting.proposalWorkflow?.sentBy?._id || meeting.proposalWorkflow?.sentBy;

    const attendeesWhoNeedToRespond = meeting.attendees?.filter(a => {
      const attendeeId = a.userId?._id || a.userId;
      return attendeeId?.toString() !== senderId?.toString();
    }) || [];

    if (attendeesWhoNeedToRespond.length === 0) return false;

    return attendeesWhoNeedToRespond.every(a =>
      a.proposalResponses && a.proposalResponses.length > 0
    );
  };

  const userAttendee = getUserAttendee();
  const hasResponded = userAttendee?.proposalResponses?.length > 0;
  const needsResponse = userAttendee && !hasResponded;
  const allHaveResponded = checkAllResponded();
  const isBKOrAdmin = ['byggkonsult', 'admin'].includes(user?.role);

  // Kolla om användaren är skaparen av mötet
  const isCreator = meeting.createdBy?._id === user?._id ||
    meeting.createdBy === user?._id;

  // BK och IA ser alla projektets möten, men andras möten är till för att läsa
  // protokollet — de ändras inte och man skriver inga anteckningar i dem.
  const mittMote = arMittMote(meeting, user);

  // Kortet är klickbart om:
  // - BK/Admin (kan hantera alla egna möten utom scheduled)
  // - Användaren skapade mötet (kan hantera sina egna utkast/föreslagna)
  // - Användaren behöver svara på proposed möte
  // - INTE för scheduled möten (använd knappar istället)
  const isScheduled = meeting.status === 'scheduled';
  const canManage = (isBKOrAdmin && mittMote) || isCreator;
  const isClickable = (canManage && !isScheduled) || needsResponse;

  // Protokollet visas på mötet. Ett utkast syns bara för den som får hantera
  // mötet — samma regel som den borttagna Protokoll-vyn hade.
  const harProtokoll = !!(meeting.hasProtocol || meeting.protocolId);
  const protokollPublicerat = meeting.protocolStatus === 'sent';
  const visaProtokoll = harProtokoll && (protokollPublicerat || canManage);
  const kanSkapaProtokoll = !harProtokoll && canManage && notesCount > 0 &&
    (meeting.status === 'scheduled' || meeting.status === 'completed');
  const skapare = meeting.createdBy?.firstName
    ? `${meeting.createdBy.firstName} ${meeting.createdBy.lastName || ''}`.trim()
    : null;

  const handleClick = () => {
    if (isClickable) {
      onClick?.(meeting);
    }
  };

  const handleReschedule = (e) => {
    e.stopPropagation();
    onReschedule?.(meeting);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(meeting);
  };

  return (
    <div
      onClick={handleClick}
      className={`
        bg-white rounded-lg border p-4 transition-all
        ${isClickable ? 'cursor-pointer hover:shadow-md hover:border-gray-300' : 'cursor-default'}
        ${allHaveResponded && canManage ? 'border-leadme-pink ring-2 ring-leadme-pink/20' :
          needsResponse ? 'border-orange-300 ring-2 ring-orange-100' :
          hasResponded ? 'border-green-300' :
          statusConfig.borderColor}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate">
            {meeting.displayName || getMeetingDisplayName(meeting)}
          </h3>
          {showProject && meeting.projectId?.projectId && (
            <p className="text-sm text-gray-500">#{meeting.projectId.projectId}</p>
          )}
          {!mittMote && skapare && (
            <p className="text-xs text-gray-400 truncate">{skapare}s möte</p>
          )}
        </div>
        {/* Status badges */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Status badge */}
          {meeting.status === 'proposed' && allHaveResponded && canManage ? (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-leadme-pink text-white animate-pulse">
              Dags att boka!
            </span>
          ) : meeting.status === 'proposed' && hasResponded && !canManage ? (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Svarat
            </span>
          ) : meeting.status === 'proposed' && needsResponse ? (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
              Väntar på svar
            </span>
          ) : (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.label}
            </span>
          )}
        </div>
      </div>

      {/* Datum/tid */}
      {meeting.scheduledAt && (
        <div className="flex items-center text-sm text-gray-600 mb-2">
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {formatScheduledAt(meeting.scheduledAt)}
        </div>
      )}

      {/* Väntar på svar (för proposed-status) */}
      {meeting.status === 'proposed' && meeting.proposalWorkflow?.proposals?.length > 0 && (
        <div className={`text-sm mb-2 ${
          allHaveResponded && canManage ? 'text-leadme-pink font-medium' :
          needsResponse ? 'text-orange-600 font-medium' :
          hasResponded ? 'text-green-600' :
          'text-yellow-600'
        }`}>
          {/* Mötesskapare/BK/Admin: Visa "Alla har svarat!" eller antal pending */}
          {canManage ? (
            allHaveResponded ? (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Alla har svarat - välj tid att boka
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Väntar på svar ({meeting.attendees?.filter(a => {
                  const senderId = meeting.proposalWorkflow?.sentBy?._id || meeting.proposalWorkflow?.sentBy;
                  const attendeeId = a.userId?._id || a.userId;
                  const isSender = attendeeId?.toString() === senderId?.toString();
                  return !isSender && (!a.proposalResponses || a.proposalResponses.length === 0);
                }).length || 0} kvar)
              </div>
            )
          ) : needsResponse ? (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-1.5 animate-pulse"></span>
              Väntar på ditt svar
            </div>
          ) : hasResponded ? (
            <div>
              <div className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Du har svarat
              </div>
              <div className="space-y-0.5 ml-5">
                {meeting.proposalWorkflow.proposals.map((proposal, idx) => {
                  const response = userAttendee.proposalResponses?.find(r => r.proposalIndex === idx);
                  return (
                    <div key={idx} className="flex items-center text-xs">
                      {response?.canAttend ? (
                        <span className="text-green-600 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {proposal.displayText || `Tid ${idx + 1}`}
                        </span>
                      ) : (
                        <span className="text-red-500 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          {proposal.displayText || `Tid ${idx + 1}`}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {meeting.proposalWorkflow.proposals.length} tidsförslag skickade
            </div>
          )}
        </div>
      )}

      {/* Plats */}
      {meeting.location && (
        <div className="flex items-center text-sm text-gray-600 mb-2">
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {meeting.teamsJoinUrl ? (
            <a href={meeting.teamsJoinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
              Microsoft Teams
            </a>
          ) : (
            meeting.location.displayName
          )}
        </div>
      )}

      {/* Varaktighet */}
      <div className="flex items-center text-sm text-gray-500 mb-3">
        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {formatDuration(meeting.duration)}
      </div>

      {/* Deltagare */}
      {meeting.attendees?.length > 0 && (
        <div className="border-t pt-3">
          <div className="flex items-center text-xs text-gray-500 mb-1">
            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            Deltagare
          </div>
          <div className="flex flex-wrap gap-1">
            {meeting.attendees.slice(0, 4).map((attendee, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-700"
                title={`${attendee.name} (${getAttendeeRoleLabel(attendee.role)})`}
              >
                {attendee.name?.split(' ')[0] || attendee.email?.split('@')[0]}
              </span>
            ))}
            {meeting.attendees.length > 4 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-500">
                +{meeting.attendees.length - 4}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Protokollet — det nås här, på mötet, och har ingen egen vy */}
      {onOpenProtocol && (visaProtokoll || kanSkapaProtokoll) && (
        <div className="border-t pt-2 mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenProtocol(meeting); }}
            className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
              visaProtokoll
                ? 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                : 'text-gray-600 border border-dashed border-gray-300 hover:border-leadme-pink hover:text-leadme-pink'
            }`}
            title={meeting.hasUnseenProtocolUpdate ? 'Någon har markerat en actionpunkt som klar i protokollet' : undefined}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              {visaProtokoll ? (
                <span className="truncate">
                  <span className="font-medium">Protokoll</span>
                  {' · '}{protokollPublicerat ? 'Publicerat' : 'Utkast'}
                </span>
              ) : (
                <span className="font-medium">Skapa protokoll</span>
              )}
            </span>
            {visaProtokoll && (
              <span className="flex items-center gap-1.5 flex-shrink-0 text-xs">
                {meeting.protocolActionTotal > 0 && (
                  <span className={meeting.protocolActionDone === meeting.protocolActionTotal ? 'text-green-700' : 'text-blue-700'}>
                    {meeting.protocolActionDone}/{meeting.protocolActionTotal} klara
                  </span>
                )}
                {meeting.hasUnseenProtocolUpdate && (
                  <span className="w-2 h-2 rounded-full bg-leadme-pink animate-pulse" />
                )}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Anteckningsindikator och snabbknapp (för scheduled/completed möten).
          Bara på egna möten — andras möten visas för protokollets skull. */}
      {mittMote && (meeting.status === 'scheduled' || meeting.status === 'completed') && (
        <div className="border-t pt-2 mt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {notesCount} anteckningar
          </span>
          {onAddNote && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddNote(meeting);
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-leadme-pink bg-white border border-leadme-pink rounded-lg hover:bg-leadme-pink/10 transition-colors"
              title="Lägg till anteckning"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Anteckning
            </button>
          )}
        </div>
      )}

      {/* Åtgärdsknappar för bokade möten (BK/admin eller skaparen) */}
      {isScheduled && canManage && (
        <div className="border-t pt-3 mt-2 flex gap-2 flex-wrap">
          <button
            onClick={handleReschedule}
            className="flex-1 px-2.5 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Ändra tid
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowAddAttendees(true); }}
            className="flex-1 px-2.5 py-1.5 text-sm font-medium text-leadme-pink bg-white border border-leadme-pink rounded-lg hover:bg-leadme-pink/10 transition-colors flex items-center justify-center gap-1"
            title="Bjud in fler deltagare till detta möte"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Bjud in
          </button>
          <button
            onClick={handleDelete}
            className="px-2.5 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Avboka
          </button>
        </div>
      )}

      {/* Modal: bjud in fler deltagare */}
      <AddAttendeesModal
        isOpen={showAddAttendees}
        onClose={() => setShowAddAttendees(false)}
        meeting={meeting}
        projectId={meeting?.project?._id || meeting?.project}
        onSuccess={() => onAttendeesAdded?.(meeting)}
      />
    </div>
  );
};

export default MeetingCard;
